// ./src/components/features/Memberships/MembershipTypeSelect/MembershipTypeSelect.tsx
//
// Reusable picker for one of the account's membership types. Loads active types
// on mount and renders a searchable SelectField on the shared (name, value)
// change contract. Clearable by default — an empty value typically means "any
// member". Used by ticket price rules; reusable anywhere a membership type is
// referenced.

import React, { useEffect, useMemo, useState } from 'react';
import { OP_RESULT_CODES } from '@sdflc/api-helpers';

import { MembershipType, MembershipTypeService } from '@/utils/api';
import { SelectField } from '@/components/generic';
import { listAllPages } from "@/utils/api";

export interface MembershipTypeSelectProps {
  name?: string;
  value?: string | null;
  onChange?: (name: string | undefined, value: string | null) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  placeholder?: string;
  clearable?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** Server-side refusal for this field — without it a listed error is swallowed. */
  error?: string | null;
}

export const MembershipTypeSelect: React.FC<MembershipTypeSelectProps> = ({
  name,
  value,
  onChange,
  label,
  description,
  placeholder,
  clearable = true,
  required,
  disabled,
  error,
}) => {
  const [types, setTypes] = useState<MembershipType[]>([]);
  const [loading, setLoading] = useState(false);

  // Load once on mount. No "loaded once" ref guard: under StrictMode's double
  // mount that ref would survive the remount and short-circuit the second effect,
  // while the first fetch's result gets dropped by the `alive` guard — leaving the
  // select stuck loading/disabled. The plain cancellation pattern is correct:
  // in prod it fetches once; in dev StrictMode fetches twice and the live one wins.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    listAllPages(MembershipTypeService.list, {})
      .then((result) => {
        if (!alive) return;
        setLoading(false);
        if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
          setTypes(result.data as MembershipType[]);
        }
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const data = useMemo(
    () => types.filter((mt) => mt.id).map((mt) => ({ value: mt.id as string, label: mt.name || '—' })),
    [types],
  );

  return (
    <SelectField
      name={name}
      value={value ?? null}
      onChange={onChange}
      label={label}
      description={description}
      placeholder={loading ? '…' : placeholder}
      data={data}
      searchable
      clearable={clearable}
      required={required}
      disabled={disabled || loading}
      error={error || undefined}
      nothingFoundMessage="No membership types"
    />
  );
};
